import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, take } from 'rxjs';

import { PlanToScheduleStatus } from '@efficiently/models';

import {
	CreatePlanResponse,
	CreatePlanWithUploadArgs,
	GetPlansResponse,
	PlansRepository,
} from '@efficiently/client-api-plans';
import { GraphQLClient } from '@efficiently/client-core-graphql';

import { CREATE_PLAN_WITH_UPLOAD } from '../queries/create-plan-with-upload';
import { GET_PLAN_TO_SCHEDULE_STATUS } from '../queries/get-plan-to-schedule-status';
import { GET_PLANS } from '../queries/get-plans';
import { RUN_PLAN_TO_SCHEDULES } from '../queries/run-plan-to-schedules';

@Injectable()
export class PlansGqlRepository extends PlansRepository {
	private readonly apollo = inject(Apollo);

	public override getPlans(projectId: string): Observable<GetPlansResponse> {
		return this.apollo
			.watchQuery({
				query: GET_PLANS,
				variables: {
					projectId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				// TEMP-CODEGEN-FRONTIER(EFF-24658): result carries the app-wide `Plan` / `PlanVersion` / `PlanItem` /
				// `PdfRequest` domain shapes.
				map(({ data }) => data.plans as unknown as GetPlansResponse),
			);
	}

	public override createPlanWithUpload(args: CreatePlanWithUploadArgs): Observable<CreatePlanResponse> {
		return this.apollo
			.mutate({
				mutation: CREATE_PLAN_WITH_UPLOAD,
				variables: { args },
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				map(({ data }) => data.createPlanWithUpload),
				take(1),
			);
	}

	public override getPlanToScheduleStatus(projectId: string): Observable<PlanToScheduleStatus> {
		return this.apollo
			.watchQuery({
				query: GET_PLAN_TO_SCHEDULE_STATUS,
				variables: {
					projectId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				// TEMP-CODEGEN-FRONTIER(EFF-24658): the document returns a plain `String` where the contract declares the
				// nominal `PlanToScheduleStatus` enum — TS enums are nominal, so the two are not assignable. Converging
				// them is the enum tech-debt line, not this sweep.
				map(({ data }) => data.getStatusPlanToSchedules as unknown as PlanToScheduleStatus),
			);
	}

	public override runPlanToSchedules(projectId: string, versionId: string): Observable<string> {
		return this.apollo
			.mutate({
				mutation: RUN_PLAN_TO_SCHEDULES,
				variables: {
					projectId,
					versionId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.runPlanToSchedules),
			);
	}
}
